import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  palette,
  Select,
  Stack,
  StatusChip,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from '@munifocus/ui';
import type { CitizenAccessBasisDto, CitizenAccessDto } from '@munifocus/contracts-gov';
import { CITIZEN_CAPABILITIES } from '@munifocus/access';
import { useConfirmAction } from '../lib/useConfirmAction';
import { useSubmitValidation } from '../lib/useSubmitValidation';
import {
  useCitizenAccess,
  useGrantCitizenAccess,
  useRevokeCitizenAccess,
  useSetCitizenCapabilities,
} from '../lib/citizen-access-api';

const BASES: CitizenAccessBasisDto[] = ['owner_of_record', 'occupant_on_site', 'served_recipient'];

/**
 * Grant or withdraw an owner/occupant's read-only Citizen Portal access.
 *
 * The basis dropdown is not bookkeeping: v47 requires identity to be confirmed
 * by "email address and property confirmation" before access is granted, and
 * this records which confirmation the officer actually performed. It is the
 * reason there is no self-service signup on the portal — nothing in the system
 * could verify a stranger's claim to own an address, but an officer can.
 */
export function CitizenAccessPanel({
  fileId,
  canGrant = true,
  canRevoke = true,
}: {
  fileId: string;
  /** False for a seat that may only read who has access. */
  canGrant?: boolean;
  canRevoke?: boolean;
}) {
  const confirm = useConfirmAction();
  const { t } = useTranslation();
  const grants = useCitizenAccess(fileId);
  const grant = useGrantCitizenAccess(fileId);
  const revoke = useRevokeCitizenAccess(fileId);
  const setCapabilities = useSetCitizenCapabilities(fileId);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [basis, setBasis] = useState<CitizenAccessBasisDto>('owner_of_record');
  const [language, setLanguage] = useState<'en' | 'fr'>('en');
  const [error, setError] = useState<string | null>(null);
  const v = useSubmitValidation({
    email: !email.trim()
      ? t('common.validation.required')
      : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
        ? null
        : t('common.validation.invalidEmail'),
    displayName: displayName.trim() ? null : t('common.validation.required'),
  });

  const sendGrant = v.guard(() =>
    grant.mutate(
      { email: email.trim(), displayName: displayName.trim(), basis, language },
      {
        onSuccess: () => {
          setEmail('');
          setDisplayName('');
          v.reset();
        },
        onError: () => setError(t('gov.citizenAccess.grantError')),
      },
    ),
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    sendGrant();
  };

  const live = grants.data?.filter((g) => !g.revokedAt) ?? [];
  const past = grants.data?.filter((g) => g.revokedAt) ?? [];

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">{t('gov.citizenAccess.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('gov.citizenAccess.hint')}
        </Typography>
      </Stack>

      {grants.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {grants.isError && <Alert severity="error">{t('gov.citizenAccess.loadError')}</Alert>}

      {grants.data && live.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('gov.citizenAccess.none')}
        </Typography>
      )}

      {live.map((g) => (
        <AccessRow
          key={g.id}
          grant={g}
          onRevoke={
            canRevoke
              ? async () => {
                  // This is a property owner's or occupant's way into the file
                  // opened against their own property; taking it away is not a
                  // tidy-up.
                  if (
                    !(await confirm({
                      title: t('gov.citizenAccess.revokeTitle'),
                      body: t('gov.citizenAccess.revokeBody', { email: g.email }),
                      confirmLabel: t('gov.citizenAccess.revokeAction'),
                      tone: 'danger',
                    }))
                  ) {
                    return;
                  }
                  revoke.mutate(g.id);
                }
              : undefined
          }
          revoking={revoke.isPending}
          onToggleCapability={
            canGrant
              ? (capability, next) =>
                  setCapabilities.mutate({
                    grantId: g.id,
                    capabilities: next
                      ? [...g.capabilities, capability]
                      : g.capabilities.filter((c) => c !== capability),
                  })
              : undefined
          }
          savingCapabilities={setCapabilities.isPending}
        />
      ))}

      {past.length > 0 && (
        <>
          <Divider />
          <Typography variant="caption" color="text.secondary">
            {t('gov.citizenAccess.revokedHeading')}
          </Typography>
          {past.map((g) => (
            <AccessRow key={g.id} grant={g} />
          ))}
        </>
      )}

      {canGrant && (
        <>
          <Divider />

          <form onSubmit={onSubmit} noValidate>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label={t('gov.citizenAccess.email')}
                {...v.field('email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                size="small"
              />
              <TextField
                label={t('gov.citizenAccess.name')}
                {...v.field('displayName')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>{t('gov.citizenAccess.basis')}</InputLabel>
                <Select
                  label={t('gov.citizenAccess.basis')}
                  aria-label={t('gov.citizenAccess.basis')}
                  value={basis}
                  onChange={(e) => setBasis(e.target.value as CitizenAccessBasisDto)}
                >
                  {BASES.map((b) => (
                    <MenuItem key={b} value={b}>
                      {t(`gov.citizenAccess.basisOption.${b}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{t('gov.citizenAccess.language')}</InputLabel>
                <Select
                  label={t('gov.citizenAccess.language')}
                  aria-label={t('gov.citizenAccess.language')}
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as 'en' | 'fr')}
                >
                  <MenuItem value="en">{t('gov.file.languageEn')}</MenuItem>
                  <MenuItem value="fr">{t('gov.file.languageFr')}</MenuItem>
                </Select>
              </FormControl>
              <Button
                type="submit"
                variant="contained"
                disabled={grant.isPending}
                sx={{ alignSelf: 'flex-start' }}
              >
                {grant.isPending
                  ? t('gov.citizenAccess.granting')
                  : t('gov.citizenAccess.grantAction')}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t('gov.citizenAccess.grantNote')}
              </Typography>
            </Stack>
          </form>
        </>
      )}
    </Stack>
  );
}

function AccessRow({
  grant,
  onRevoke,
  revoking,
  onToggleCapability,
  savingCapabilities,
}: {
  grant: CitizenAccessDto;
  onRevoke?: () => void;
  revoking?: boolean;
  onToggleCapability?: (capability: string, next: boolean) => void;
  savingCapabilities?: boolean;
}) {
  const { t } = useTranslation();
  const revoked = !!grant.revokedAt;

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: revoked ? palette.text.muted : undefined }}>
            {grant.displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {grant.email} · {t(`gov.citizenAccess.basisOption.${grant.basis}`)}
          </Typography>
        </Stack>
        {revoked ? (
          <StatusChip status="neutral" label={t('gov.citizenAccess.revoked')} />
        ) : onRevoke ? (
          <Button size="small" color="error" onClick={onRevoke} disabled={revoking}>
            {t('gov.citizenAccess.revokeAction')}
          </Button>
        ) : null}
      </Stack>

      {/* Only on a live grant. A revoked one permits nothing regardless, and
          showing switches that cannot mean anything invites an officer to
          believe they have changed something. */}
      {!revoked && onToggleCapability && (
        <Stack sx={{ pl: 0.5 }}>
          {CITIZEN_CAPABILITIES.map((capability) => (
            <FormControlLabel
              key={capability}
              control={
                <Switch
                  size="small"
                  checked={grant.capabilities.includes(capability)}
                  disabled={savingCapabilities}
                  onChange={(e) => onToggleCapability(capability, e.target.checked)}
                  slotProps={{
                    input: { 'aria-label': t(`gov.citizenAccess.capability.${capability}`) },
                  }}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  {t(`gov.citizenAccess.capability.${capability}`)}
                </Typography>
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
